<template>
  <div
    v-if="message.text && (message.type === 'human' || message.type === 'feedback')"
    class="message-text"
  >
    <span class="sr-only">I say: </span>{{ message.text }}
  </div>
  <div
    v-else-if="altHtmlMessage && AllowSuperDangerousHTMLInMessage"
    v-html="altHtmlMessage"
    class="message-text"
  ></div>
  <div
    v-else-if="message.text && shouldRenderAsHtml"
    v-html="botMessageAsHtml"
    class="message-text"
  ></div>
  <div
    v-else-if="message.text && (message.type === 'bot' || message.type === 'agent')"
    class="message-text bot-message-plain"
  >
    <span class="sr-only">{{ message.type }} says: </span>{{ (shouldStripTags) ? stripTagsFromMessage(message.text) : message.text }}
  </div>
</template>

<script>
/*
Copyright 2017-2019 Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Amazon Software License (the "License"). You may not use this file
except in compliance with the License. A copy of the License is located at

http://aws.amazon.com/asl/

or in the "license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. See the
License for the specific language governing permissions and limitations under the License.
*/
import { chatMode } from '@/store/state';
const marked = require('marked');
const renderer = {};
renderer.link = function link(href, title, text) {
  return `<a href="${href}" title="${title}" target="_blank">${text}</a>`;
};
marked.use({renderer});

export default {
  name: 'message-text',
  props: ['message'],
  computed: {
    shouldConvertUrlToLinks() {
      return this.$store.state.config.ui.convertUrlToLinksInBotMessages;
    },
    shouldStripTags() {
      return this.$store.state.config.ui.stripTagsFromBotMessages;
    },
    AllowSuperDangerousHTMLInMessage() {
      return this.$store.state.config.ui.AllowSuperDangerousHTMLInMessage;
    },
    altHtmlMessage() {
      let out = false;
      if (this.message.alts) {
        if (this.message.alts.html) {
          out = this.message.alts.html;
        } else if (this.message.alts.markdown) {
          out = marked.parse(this.message.alts.markdown);
        }
      }
      if (out) out = this.prependBotScreenReader(out);
      return out;
    },
    shouldRenderAsHtml() {
      return (['bot', 'agent'].includes(this.message.type) && this.shouldConvertUrlToLinks);
    },
    botMessageAsHtml() {
      // Security Note: Make sure that the content is escaped according
      // to context (e.g. URL, HTML). This is rendered as HTML
      const messageText = this.stripTagsFromMessage(this.message.text);
      const messageWithLinks = this.botMessageWithLinks(messageText);
      const messageWithSR = this.prependBotScreenReader(messageWithLinks);
      return messageWithSR;
    },
  },
  methods: {
    encodeAsHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    formatText(value) {
      const encoded = this.encodeAsHtml(value);
      return encoded.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    },
    botMessageWithLinks(messageText) {
      const markdownLinkPattern = /\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^)\s]+)\)/gim;
      const plainLinkPattern = new RegExp(
        "\\b((?:https?://\\w{1}|www\\.)(?:[\\w-.]){2,256}" +
        "(?:[\\w._~:/?#@!$&()*+,;=[\\'\\]-]){0,256})" +
        "|\\b([\\w.!#$%&'*+/=?^_{}|~-]+@[\\w.-]+\\.[A-Za-z]{2,})",
        'gim',
      );
      const markdownLinks = [];
      const protectedText = messageText.replace(markdownLinkPattern, (match, markdownLabel, markdownHref) => {
        const href = markdownHref.toLowerCase().startsWith('mailto:')
          ? `mailto:${encodeURIComponent(markdownHref.slice(7)).replace(/%40/g, '@')}`
          : encodeURI(markdownHref);
        markdownLinks.push('<a target="_blank" ' +
          `href="${this.encodeAsHtml(href)}">${this.formatText(markdownLabel)}</a>`);
        return `__SOPHIE_MARKDOWN_LINK_${markdownLinks.length - 1}__`;
      });

      let output = '';
      let lastIndex = 0;
      protectedText.replace(plainLinkPattern, (match, webUrl, email, offset) => {
        const precedingText = protectedText.slice(lastIndex, offset);
        const markdownPrefix = webUrl && precedingText.match(/\[([^\]]+)\]\($/);
        output += this.formatText(markdownPrefix
          ? precedingText.slice(0, precedingText.length - markdownPrefix[0].length)
          : precedingText);
        if (markdownPrefix) {
          const href = (!/^https?:\/\//.test(webUrl)) ? `http://${webUrl}` : webUrl;
          output += '<a target="_blank" ' +
            `href="${this.encodeAsHtml(encodeURI(href))}">${this.formatText(markdownPrefix[1])}</a>`;
          lastIndex = offset + match.length + (protectedText[offset + match.length] === ')' ? 1 : 0);
          return match;
        }
        if (webUrl) {
          const url = (!/^https?:\/\//.test(webUrl)) ? `http://${webUrl}` : webUrl;
          const labelMatch = protectedText.slice(offset + match.length).match(/^\s*\(([^)]+)\)/);
          output += '<a target="_blank" ' +
            `href="${this.encodeAsHtml(encodeURI(url))}">${labelMatch
              ? this.formatText(labelMatch[1])
              : this.encodeAsHtml(webUrl)}</a>`;
          if (labelMatch) {
            lastIndex = offset + match.length + labelMatch[0].length;
            return match;
          }
        } else {
          const mailto = encodeURIComponent(email).replace(/%40/g, '@');
          output += '<a target="_blank" ' +
            `href="mailto:${this.encodeAsHtml(mailto)}">${this.encodeAsHtml(email)}</a>`;
        }
        lastIndex = offset + match.length;
        return match;
      });
      output += this.formatText(protectedText.slice(lastIndex));

      return output.replace(/__SOPHIE_MARKDOWN_LINK_(\d+)__/g, (match, index) => markdownLinks[index]);
    },
    // used for stripping SSML (and other) tags from bot responses
    stripTagsFromMessage(messageText) {
      const doc = document.implementation.createHTMLDocument('').body;
      let text = messageText;
      // Some Bedrock responses contain HTML that has already been entity-encoded.
      // Decode once, then strip any markup exposed by that decode.
      for (let pass = 0; pass < 2; pass += 1) {
        doc.innerHTML = text;
        const decodedText = doc.textContent || doc.innerText || '';
        if (decodedText === text) break;
        text = decodedText;
      }
      return text;
    },
    isLiveChat() {
      return (this.$store.state.config.ui.enableLiveChat &&
        this.$store.state.chatMode === chatMode.LIVECHAT);
    },
    prependBotScreenReader(messageText) {
      if (this.isLiveChat()) {
        return `<span class="sr-only">agent says: </span>${messageText}`;
      } else {
        return `<span class="sr-only">bot says: </span>${messageText}`;
      }
    },
  },
};
</script>

<style scoped>
.message-text {
  hyphens: auto;
  overflow-wrap: break-word;
  padding: 0.8em;
  white-space: normal;
  word-break: break-word;
  width: 100%;
}

.message-text :deep(p) {
  margin-bottom: 16px;
}
</style>

<style>
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(1px, 1px, 1px, 1px) !important;
  clip-path: inset(50%) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
